import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  writeBatch,
  limit,
  where
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { db, storage, shouldSkipFirestore } from './config'
import { getCurrentUser } from './auth'
import { logger } from '@/utils/logger'
import type { Testimonial } from '@/types/testimonial'

// Testimonials abrufen (nur sichtbare für öffentliche Ansicht)
export const getTestimonials = async (onlyVisible: boolean = true): Promise<Testimonial[]> => {
  // Für Bots/Performance-Tools: Keine Firestore-Abfrage, um Timeouts zu vermeiden
  if (shouldSkipFirestore || !db) {
    logger.debug('Firestore übersprungen - Testimonials werden nicht geladen');
    return [];
  }
  
  let q = query(collection(db, 'testimonials'), orderBy('order', 'asc'))
  
  if (onlyVisible) {
    q = query(collection(db, 'testimonials'), where('isVisible', '==', true), orderBy('order', 'asc'))
  }
  
  const querySnapshot = await getDocs(q)
  
  return querySnapshot.docs.map(doc => {
    const data = doc.data()
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    } as Testimonial
  })
}

// Testimonial erstellen
export const createTestimonial = async (testimonial: Omit<Testimonial, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const testimonialData: any = {
    name: testimonial.name,
    role: testimonial.role,
    company: testimonial.company,
    imageUrl: testimonial.imageUrl,
    text: testimonial.text,
    order: testimonial.order,
    isVisible: testimonial.isVisible,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  }
  
  // Füge optionale Felder nur hinzu, wenn sie definiert sind
  if (testimonial.rating !== undefined && testimonial.rating !== null) {
    testimonialData.rating = testimonial.rating
  }
  if (testimonial.projectId) {
    testimonialData.projectId = testimonial.projectId
  }
  
  const docRef = await addDoc(collection(db, 'testimonials'), testimonialData)
  return docRef.id
}

// Testimonial aktualisieren
export const updateTestimonial = async (id: string, testimonial: Partial<Testimonial>): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const docRef = doc(db, 'testimonials', id)
  
  const updateData: Record<string, any> = {
    updatedAt: Timestamp.now()
  }
  
  // Füge nur definierte Felder hinzu
  if (testimonial.name !== undefined) updateData.name = testimonial.name
  if (testimonial.role !== undefined) updateData.role = testimonial.role
  if (testimonial.company !== undefined) updateData.company = testimonial.company
  if (testimonial.imageUrl !== undefined) updateData.imageUrl = testimonial.imageUrl
  if (testimonial.text !== undefined) updateData.text = testimonial.text
  if (testimonial.rating !== undefined) {
    updateData.rating = testimonial.rating || null
  }
  if (testimonial.projectId !== undefined) {
    updateData.projectId = testimonial.projectId || null
  }
  if (testimonial.order !== undefined) updateData.order = testimonial.order
  if (testimonial.isVisible !== undefined) updateData.isVisible = testimonial.isVisible
  
  await updateDoc(docRef, updateData)
}

// Testimonial löschen
export const deleteTestimonial = async (id: string, imageUrl: string): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  // Bild aus Storage löschen
  try {
    const urlObj = new URL(imageUrl)
    const pathMatch = urlObj.pathname.match(/\/o\/(.+)/)
    if (pathMatch) {
      const decodedPath = decodeURIComponent(pathMatch[1])
      const imageRef = ref(storage, decodedPath)
      await deleteObject(imageRef)
    }
  } catch (error) {
    logger.error('Fehler beim Löschen des Testimonial-Bildes aus Storage', error)
  }
  
  // Dokument aus Firestore löschen
  const docRef = doc(db, 'testimonials', id)
  await deleteDoc(docRef)
}

// Testimonial-Bild hochladen
export const uploadTestimonialImage = async (file: File, testimonialId: string): Promise<string> => {
  const user = getCurrentUser()
  if (!user) throw new Error('Nicht authentifiziert')
  await user.getIdToken(true)

  const fileExtension = file.name.split('.').pop()
  const fileName = `testimonials/${testimonialId}/image-${Date.now()}.${fileExtension}`
  const storageRef = ref(storage, fileName)
  
  await uploadBytes(storageRef, file)
  return await getDownloadURL(storageRef)
}

// Testimonial-Bild löschen (nur aus Storage)
export const deleteTestimonialImage = async (imageUrl: string): Promise<void> => {
  try {
    const urlObj = new URL(imageUrl)
    const pathMatch = urlObj.pathname.match(/\/o\/(.+)/)
    if (pathMatch) {
      const decodedPath = decodeURIComponent(pathMatch[1])
      const imageRef = ref(storage, decodedPath)
      await deleteObject(imageRef)
    }
  } catch (error) {
    logger.error('Fehler beim Löschen des Testimonial-Bildes aus Storage', error)
    throw error
  }
}

// Batch-Update für Order-Änderungen
export const reorderTestimonials = async (
  updates: { id: string; order: number }[]
): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  // Lokale Variable für TypeScript-Narrowing
  const firestore = db
  const batch = writeBatch(firestore)
  
  updates.forEach(update => {
    const docRef = doc(firestore, 'testimonials', update.id)
    batch.update(docRef, { 
      order: update.order,
      updatedAt: Timestamp.now()
    })
  })
  
  await batch.commit()
}

// Sichtbarkeit umschalten
export const toggleTestimonialVisibility = async (
  id: string, 
  isVisible: boolean
): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const docRef = doc(db, 'testimonials', id)
  await updateDoc(docRef, { 
    isVisible,
    updatedAt: Timestamp.now()
  })
}

// Nächste verfügbare Order-Nummer
export const getNextTestimonialOrder = async (): Promise<number> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const q = query(
    collection(db, 'testimonials'), 
    orderBy('order', 'desc'),
    limit(1)
  )
  
  const querySnapshot = await getDocs(q)
  if (querySnapshot.empty) return 1
  
  const lastTestimonial = querySnapshot.docs[0].data()
  return (lastTestimonial.order || 0) + 1
}

